import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { RESERVATION_TYPE, type ReservationType } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { CellDatetime, DataTableColumnHeader } from 'src/registry/components/table';
import { Badge } from 'src/registry/ui/Badge';
import { generalPriceMessages } from 'src/utils/messages';

const columnHelper = createColumnHelper<ReservationType>();

const reservationTypeTranslationMap = {
    [RESERVATION_TYPE.HOURLY]: reservationMessages.hourly,
    [RESERVATION_TYPE.DAILY]: reservationMessages.daily,
    [RESERVATION_TYPE.FIXED_DURATION]: reservationMessages.fixedDuration,
};

export const ReservationTypeTableColumns = () => {
    const { formatMessage, formatNumber } = useIntl();

    return [
        columnHelper.accessor('reservation_type', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const reservationType: RESERVATION_TYPE = row.original.reservation_type;
                return (
                    <div className="flex">
                        <div className="mr-2 flex-1">
                            {formatMessage(reservationTypeTranslationMap[reservationType])}
                        </div>
                        {row.original.valid_to === null && row.original.valid_from ? (
                            <div className="flex">
                                <Badge className="ml-2 max-w-40 flex-1 whitespace-nowrap">
                                    <div className="size-1.5 rounded-full bg-emerald-600" />
                                    <div>{formatMessage(reservationMessages.latestVersion)}</div>
                                </Badge>
                            </div>
                        ) : null}
                    </div>
                );
            },
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.name),
            },
        }),
        columnHelper.accessor('enabled', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const iconColor = row.original.enabled ? 'bg-emerald-600' : 'bg-red-600';
                const badgeText = row.original.enabled
                    ? formatMessage(reservationMessages.active)
                    : formatMessage(reservationMessages.inactive);
                return (
                    <Badge>
                        <div className={`size-1.5 rounded-full ${iconColor}`}></div>
                        {badgeText}
                    </Badge>
                );
            },
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.status),
            },
            id: 'active_status',
        }),
        columnHelper.accessor('price_with_tax', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return (
                    <div className="flex whitespace-nowrap">
                        {row.original.price_with_tax
                            ? `${formatNumber(row.original.price_with_tax / 100, {
                                  style: 'currency',
                                  currency: row.original.currency,
                              })}`
                            : ''}
                    </div>
                );
            },
            enableHiding: true,
            meta: {
                title: formatMessage(generalPriceMessages.price),
            },
        }),
        columnHelper.accessor('scheduled_versions_count', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const versionNumber = row.original.scheduled_versions_count || 0;
                const versionTranslation =
                    versionNumber > 0 && versionNumber < 2
                        ? formatMessage(reservationMessages.version)
                        : formatMessage(reservationMessages.versions);
                return `${versionNumber} ${versionTranslation}`;
            },
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.scheduledChanges),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.reservationValidFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.reservationValidTo),
            },
        }),
    ];
};
